import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTheme } from '@/context/ThemeContext';
import { useStudentAuth } from '@/context/StudentAuthContext';
import { useAssetContext } from '@/context/AssetContext';
import SEO from '@/components/SEO';
import { 
  Search, 
  BookMarked, 
  FileText, 
  LogOut,
  ChevronRight,
  Sparkles,
  User,
  Bell,
  Calculator,
  Banknote,
  MessageCircle,
  AlertCircle,
  Phone,
  Lock,
  Bookmark,
  BookmarkX,
  MapPin,
  ExternalLink,
  ClipboardList,
  Clock,
  CheckCircle2,
  ArrowRight,
  Target,
  Compass,
  History,
  X,
  BookOpen,
  Languages
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import RecentActivity from '@/components/dashboard/RecentActivity';
import PortalLeadGate from '@/components/PortalLeadGate';
import PersonaWelcomeBanner from '@/components/dashboard/PersonaWelcomeBanner';
import PersonaSelectorModal from '@/components/dashboard/PersonaSelectorModal';

const API_URL = window.location.origin;

const StudentDashboard = () => {
  const { isDark } = useTheme();
  const { user, logout, loading, isAuthenticated, refreshUser } = useStudentAuth();
  const { getLogo } = useAssetContext();
  const navigate = useNavigate();
  
  // States
  const [searchStatus, setSearchStatus] = useState(null);
  const [savedUniversities, setSavedUniversities] = useState([]);
  const [savedSearches, setSavedSearches] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loadingSaved, setLoadingSaved] = useState(true);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [showPersonaModal, setShowPersonaModal] = useState(false);
  const [userMotivation, setUserMotivation] = useState(null);
  
  const isLockedOut = searchStatus?.is_blocked === true;
  
  // Get user's motivation from their profile
  useEffect(() => {
    if (user?.primary_motivation) {
      setUserMotivation(user.primary_motivation);
    } else if (user?.persona) {
      setUserMotivation(user.persona);
    }
  }, [user]);
  
  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/uac-desk/login');
    }
  }, [loading, isAuthenticated, navigate]);
  
  // Fetch all dashboard data
  useEffect(() => {
    const fetchDashboardData = async () => {
      if (!user?.user_id) return;
      
      try {
        // Parallel fetch for better performance
        const [searchRes, savedRes, appsRes, savedSearchRes] = await Promise.all([
          fetch(`${API_URL}/api/search/status/${user.user_id}`),
          fetch(`${API_URL}/api/university-pages/saved/${user.user_id}`),
          fetch(`${API_URL}/api/applications/user/${user.user_id}?active_only=false`),
          fetch(`${API_URL}/api/ai/searches/list`, { credentials: 'include' })
        ]);
        
        if (searchRes.ok) setSearchStatus(await searchRes.json());
        if (savedRes.ok) {
          const data = await savedRes.json();
          setSavedUniversities(data.saved_universities || []);
        }
        if (appsRes.ok) {
          const data = await appsRes.json();
          setApplications(data.applications || []);
        }
        if (savedSearchRes.ok) {
          const data = await savedSearchRes.json();
          setSavedSearches(data.searches || []);
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoadingSaved(false);
      }
    };
    
    fetchDashboardData();
  }, [user]);
  
  const handleRemoveSaved = async (slug, universityName) => {
    try {
      const res = await fetch(`${API_URL}/api/university-pages/saved/${user.user_id}/${slug}`, {
        method: 'DELETE'
      });
      if (res.ok) {
        setSavedUniversities(prev => prev.filter(u => u.slug !== slug));
        toast.success(`Removed ${universityName} from saved list`);
      }
    } catch (error) {
      console.error('Error removing saved university:', error);
      toast.error('Failed to remove university');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/uac-desk/login');
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-[#1a2830]' : 'bg-slate-50'}`}>
        <div className="animate-spin w-8 h-8 border-4 border-ux-cyan border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!user) return null;
  
  // Calculate application stats
  const activeApps = applications.filter(a => a.is_active);
  const completedApps = applications.filter(a => a.outcome === 'success');
  const hasApplications = applications.length > 0;

  return (
    <>
      <SEO 
        title="Student Dashboard"
        description="Your UniXperts student portal - track applications, find universities, access AI counselor, and manage your study abroad journey."
        noindex={true}
      />
      <PortalLeadGate
        userEmail={user?.email}
        userName={user?.name}
        userPhone={user?.phone || ''}
        userId={user?.user_id || ''}
      >
      <div className={`min-h-screen w-full max-w-[100vw] overflow-x-hidden ${isDark ? 'bg-[#1a2830]' : 'bg-slate-50'}`}>
      {/* Header */}
      <header className={`sticky top-0 z-50 border-b w-full ${isDark ? 'bg-[#233842] border-white/10' : 'bg-white border-slate-200 shadow-sm'}`}>
        <div className="w-full max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center">
              <img src={getLogo()} alt="UniXperts" className={`h-8 w-auto object-contain ${isDark ? 'brightness-0 invert' : ''}`} />
            </Link>

            <div className="flex items-center gap-3 sm:gap-4">
              {searchStatus && (
                <button
                  onClick={() => navigate('/uac-desk/search-history')}
                  className={`hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs transition-colors ${
                    searchStatus.is_blocked 
                      ? 'bg-rose-100 text-rose-700 hover:bg-rose-200' 
                      : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                  }`}
                >
                  <Search className="w-3 h-3" />
                  <span>{searchStatus.searches_remaining} left</span>
                </button>
              )}
              
              <Button variant="ghost" size="icon" className={isDark ? 'text-white/70 hover:text-white' : ''}>
                <Bell className="w-5 h-5" />
              </Button>
              
              <div className="flex items-center gap-2 sm:gap-3">
                <Link to="/uac-desk/fast-track" className="group">
                  <Avatar className="w-9 h-9 ring-2 ring-transparent group-hover:ring-ux-cyan transition-all">
                    <AvatarImage src={user.picture} />
                    <AvatarFallback className="bg-gradient-to-br from-ux-cyan to-ux-blue text-white text-sm">
                      {user.name?.charAt(0) || 'U'}
                    </AvatarFallback>
                  </Avatar>
                </Link>
                <div className="hidden sm:block">
                  <Link to="/uac-desk/fast-track" className={`text-sm font-medium hover:text-ux-cyan transition-colors ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                    {user.name?.split(' ')[0]}
                  </Link>
                </div>
              </div>

              <Button 
                variant="ghost" 
                size="icon"
                onClick={handleLogout}
                className={isDark ? 'text-white/70 hover:text-white' : ''}
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Personalized Welcome Banner based on motivation */}
        {userMotivation && (
          <PersonaWelcomeBanner
            userMotivation={userMotivation}
            userName={user.name?.split(' ')[0]}
            isDark={isDark}
            onChangePersona={() => setShowPersonaModal(true)}
          />
        )}
        
        {/* Persona Selector Modal */}
        <PersonaSelectorModal
          isOpen={showPersonaModal}
          onClose={() => setShowPersonaModal(false)}
          currentPersona={userMotivation}
          userId={user?.user_id}
          isDark={isDark}
          onPersonaChange={(newPersona) => {
            setUserMotivation(newPersona);
            // Refresh user data
            if (refreshUser) refreshUser();
          }}
        />
        
        {/* Welcome (shown only if no persona set) */}
        {!userMotivation && (
          <div className="mb-6 sm:mb-8">
            <h1 className={`text-xl sm:text-2xl md:text-3xl font-bold ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
              Welcome back, {user.name?.split(' ')[0]}!
            </h1>
            <p className={`mt-1 text-sm sm:text-base ${isDark ? 'text-white/60' : 'text-ux-charcoal/60'}`}>
              Your study abroad journey starts here
            </p>
            {/* Prompt to set focus */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowPersonaModal(true)}
              className="mt-3"
            >
              Set your focus to personalize your experience
            </Button>
          </div>
        )}

        {/* ========== MY APPLICATIONS - Most Prominent ========== */}
        <section className="mb-6 sm:mb-8">
          <div className={`rounded-2xl sm:rounded-3xl overflow-hidden ${
            hasApplications 
              ? 'bg-gradient-to-br from-ux-cyan/10 via-ux-blue/10 to-ux-violet/10 border-2 border-ux-cyan/30' 
              : isDark ? 'bg-white/5 border border-white/10' : 'bg-white border border-gray-200'
          }`}>
            <div className="p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-3 sm:gap-4">
                  <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl flex items-center justify-center ${
                    hasApplications 
                      ? 'bg-gradient-to-br from-ux-cyan to-ux-blue' 
                      : isDark ? 'bg-white/10' : 'bg-gray-100'
                  }`}>
                    <ClipboardList className={`w-6 h-6 sm:w-7 sm:h-7 ${hasApplications ? 'text-white' : isDark ? 'text-white/60' : 'text-gray-400'}`} />
                  </div>
                  <div>
                    <h2 className={`text-lg sm:text-xl font-bold ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                      My Applications
                    </h2>
                    {hasApplications ? (
                      <div className="flex items-center gap-3 mt-1">
                        <span className={`text-sm ${isDark ? 'text-white/70' : 'text-ux-charcoal/70'}`}>
                          {activeApps.length} active
                        </span>
                        {completedApps.length > 0 && (
                          <span className="text-sm text-emerald-600 flex items-center gap-1">
                            <CheckCircle2 className="w-3.5 h-3.5" />
                            {completedApps.length} successful
                          </span>
                        )}
                      </div>
                    ) : (
                      <p className={`text-sm ${isDark ? 'text-white/50' : 'text-ux-charcoal/50'}`}>
                        No applications yet
                      </p>
                    )}
                  </div>
                </div>
                <Button 
                  onClick={() => navigate('/uac-desk/applications')}
                  className={hasApplications 
                    ? 'bg-gradient-to-r from-ux-cyan to-ux-blue hover:opacity-90 text-white'
                    : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                  }
                  data-testid="applications-btn"
                >
                  {hasApplications ? 'Track Progress' : 'View Applications'}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
              
              {/* Recent applications preview */}
              {activeApps.length > 0 && (
                <div className="mt-4 pt-4 border-t border-ux-cyan/20">
                  <div className="flex overflow-x-auto gap-3 pb-2 -mx-1 px-1 no-scrollbar">
                    {activeApps.slice(0, 3).map((app) => (
                      <div 
                        key={app.application_id}
                        className={`flex-shrink-0 p-3 rounded-xl ${isDark ? 'bg-white/5' : 'bg-white'} min-w-[200px] max-w-[250px]`}
                      >
                        <p className={`text-sm font-medium truncate ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                          {app.university_name}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge className={`text-xs ${
                            app.stage_info?.color === 'green' ? 'bg-emerald-100 text-emerald-700' :
                            app.stage_info?.color === 'orange' ? 'bg-amber-100 text-amber-700' :
                            'bg-blue-100 text-blue-700'
                          }`}>
                            {app.stage_info?.name || app.current_stage}
                          </Badge>
                        </div>
                      </div>
                    ))}
                    {activeApps.length > 3 && (
                      <div className={`flex-shrink-0 p-3 rounded-xl ${isDark ? 'bg-white/5' : 'bg-gray-50'} min-w-[100px] flex items-center justify-center`}>
                        <span className={`text-sm ${isDark ? 'text-white/60' : 'text-ux-charcoal/60'}`}>
                          +{activeApps.length - 3} more
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* ========== START YOUR JOURNEY - AI Finder + Chat with Counsellor ========== */}
        <section className="mb-6 sm:mb-8">
          <h3 className={`text-sm font-medium uppercase tracking-wider mb-4 ${isDark ? 'text-white/50' : 'text-ux-charcoal/50'}`}>
            Start Your Journey
          </h3>
          
          <div className="grid md:grid-cols-2 gap-4 sm:gap-6">
            {/* AI University Finder - Start Application */}
            <div 
              className={`group relative rounded-2xl sm:rounded-3xl p-5 sm:p-6 transition-all cursor-pointer overflow-hidden ${
                isLockedOut 
                  ? 'opacity-60 cursor-not-allowed'
                  : 'hover:shadow-xl hover:scale-[1.01]'
              } ${isDark ? 'bg-white/5 border border-white/10' : 'bg-white border border-gray-200 shadow-sm'}`}
              onClick={() => !isLockedOut && navigate('/uac-desk/ai-finder')}
              data-testid="ai-finder-card"
            >
              {/* Background gradient on hover */}
              <div className="absolute inset-0 bg-gradient-to-br from-ux-cyan/5 via-transparent to-ux-blue/5 opacity-0 group-hover:opacity-100 transition-opacity" />
              
              <div className="relative">
                <div className="flex items-start justify-between mb-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                    isLockedOut ? 'bg-gray-200' : 'bg-gradient-to-br from-ux-cyan to-ux-blue'
                  }`}>
                    {isLockedOut ? <Lock className="w-5 h-5 text-gray-500" /> : <Target className="w-6 h-6 text-white" />}
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-gradient-to-r from-ux-cyan to-ux-blue text-white text-xs font-semibold px-2.5 py-1">
                      Start Application
                    </Badge>
                    {!isLockedOut && searchStatus && (
                      <Badge className="bg-emerald-100 text-emerald-700 text-xs">
                        {searchStatus.searches_remaining} left
                      </Badge>
                    )}
                    {isLockedOut && (
                      <Badge className="bg-gray-200 text-gray-600 text-xs">Limit reached</Badge>
                    )}
                  </div>
                </div>
                
                <h3 className={`text-lg sm:text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                  AI University Finder
                </h3>
                <p className={`text-sm mb-4 ${isDark ? 'text-white/60' : 'text-ux-charcoal/60'}`}>
                  Answer a few quick questions and get matched with universities. Save courses to drafts or apply directly.
                </p>
                
                <div className={`flex items-center gap-4 text-xs ${isDark ? 'text-white/40' : 'text-ux-charcoal/40'}`}>
                  <span className="flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5" />
                    ~2 min
                  </span>
                  <span className="flex items-center gap-1">
                    <Sparkles className="w-3.5 h-3.5" />
                    AI-powered
                  </span>
                </div>
                
                <div className={`mt-4 pt-4 border-t ${isDark ? 'border-white/10' : 'border-gray-100'}`}>
                  <p className={`text-xs ${isDark ? 'text-ux-cyan' : 'text-ux-blue'}`}>
                    Best for: Finding universities & starting your application
                  </p>
                </div>
              </div>
            </div>

            {/* Chat with Counsellor - Opens Zoho SalesIQ */}
            <button 
              onClick={() => {
                // Trigger Zoho SalesIQ chat widget
                const floatBtn = document.getElementById('zsiq_float');
                if (floatBtn) {
                  const clickEvent = new MouseEvent('click', {
                    view: window,
                    bubbles: true,
                    cancelable: true
                  });
                  floatBtn.dispatchEvent(clickEvent);
                } else {
                  // Fallback: try SalesIQ API
                  if (window.$zoho && window.$zoho.salesiq) {
                    window.$zoho.salesiq.floatwindow.visible('show');
                  }
                }
              }}
              className={`group relative rounded-2xl sm:rounded-3xl p-5 sm:p-6 transition-all cursor-pointer overflow-hidden hover:shadow-xl hover:scale-[1.01] text-left ${
                isDark ? 'bg-white/5 border border-white/10' : 'bg-white border border-gray-200 shadow-sm'
              }`}
              data-testid="chat-counsellor-card"
            >
              {/* Background gradient on hover */}
              <div className="absolute inset-0 bg-gradient-to-br from-ux-green/5 via-transparent to-emerald-500/5 opacity-0 group-hover:opacity-100 transition-opacity" />
              
              <div className="relative">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-ux-green to-emerald-500 flex items-center justify-center">
                    <MessageCircle className="w-6 h-6 text-white" />
                  </div>
                  <Badge className="bg-emerald-100 text-emerald-700 text-xs">Live Support</Badge>
                </div>
                
                <h3 className={`text-lg sm:text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                  Chat with Counsellor
                </h3>
                <p className={`text-sm mb-4 ${isDark ? 'text-white/60' : 'text-ux-charcoal/60'}`}>
                  Have questions? Chat directly with your assigned counsellor for personalized guidance and support.
                </p>
                
                <div className={`flex items-center gap-4 text-xs ${isDark ? 'text-white/40' : 'text-ux-charcoal/40'}`}>
                  <span className="flex items-center gap-1">
                    <User className="w-3.5 h-3.5" />
                    Expert advice
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5" />
                    Quick response
                  </span>
                </div>
                
                <div className={`mt-4 pt-4 border-t ${isDark ? 'border-white/10' : 'border-gray-100'}`}>
                  <p className={`text-xs ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>
                    Best for: Getting help & personalized guidance
                  </p>
                </div>
              </div>
            </button>
          </div>
        </section>

        {/* ========== SAVED SEARCHES - New Feature ========== */}
        {savedSearches.length > 0 && (
          <section className="mb-6 sm:mb-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className={`text-sm font-medium uppercase tracking-wider flex items-center gap-2 ${isDark ? 'text-white/50' : 'text-ux-charcoal/50'}`}>
                <History className="w-4 h-4" />
                Saved Searches
              </h3>
              <Link to="/uac-desk/search-history" className="text-xs text-ux-cyan hover:underline flex items-center gap-1">
                View all <ChevronRight className="w-3 h-3" />
              </Link>
            </div>
            
            <div className="flex overflow-x-auto gap-3 pb-2 -mx-1 px-1 no-scrollbar">
              {savedSearches.slice(0, 4).map((search) => (
                <div 
                  key={search.id}
                  onClick={() => navigate(`/uac-desk/ai-finder?load=${search.id}`)}
                  className={`flex-shrink-0 p-4 rounded-xl cursor-pointer transition-all hover:scale-[1.02] ${
                    isDark ? 'bg-white/5 hover:bg-white/10' : 'bg-white hover:shadow-md border border-gray-200'
                  } min-w-[220px] max-w-[280px]`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Search className={`w-4 h-4 ${isDark ? 'text-ux-cyan' : 'text-ux-blue'}`} />
                    <span className={`text-xs ${isDark ? 'text-white/40' : 'text-ux-charcoal/40'}`}>
                      {search.created_at ? new Date(search.created_at).toLocaleDateString() : ''}
                    </span>
                  </div>
                  <p className={`text-sm font-medium truncate ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                    {search.search_name || 'Saved Search'}
                  </p>
                  <p className={`text-xs truncate ${isDark ? 'text-white/50' : 'text-ux-charcoal/50'}`}>
                    {search.countries?.join(', ') || 'Multiple countries'}
                  </p>
                  {search.num_recommendations > 0 && (
                    <Badge className="mt-2 bg-ux-cyan/10 text-ux-cyan text-xs">
                      {search.num_recommendations} results
                    </Badge>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ========== TOOLS & RESOURCES ========== */}
        <section className="mb-6 sm:mb-8">
          <h3 className={`text-sm font-medium uppercase tracking-wider mb-4 ${isDark ? 'text-white/50' : 'text-ux-charcoal/50'}`}>
            Tools & Resources
          </h3>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
            {/* GPA Calculator - NEW */}
            <Link 
              to="/uac-desk/gpa-calculator"
              className={`group p-4 sm:p-5 rounded-xl sm:rounded-2xl transition-all hover:scale-[1.02] ${
                isDark ? 'bg-white/5 hover:bg-white/10' : 'bg-white hover:shadow-lg border border-gray-200'
              }`}
              data-testid="gpa-tool"
            >
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-ux-violet to-purple-600 flex items-center justify-center mb-3">
                <Calculator className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <h4 className={`font-semibold text-sm sm:text-base mb-1 ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                GPA Calculator
              </h4>
              <p className={`text-xs ${isDark ? 'text-white/50' : 'text-ux-charcoal/50'}`}>
                Convert scores
              </p>
            </Link>

            {/* Loan Eligibility */}
            <Link 
              to="/uac-desk/loan-eligibility"
              className={`group p-4 sm:p-5 rounded-xl sm:rounded-2xl transition-all hover:scale-[1.02] ${
                isDark ? 'bg-white/5 hover:bg-white/10' : 'bg-white hover:shadow-lg border border-gray-200'
              }`}
              data-testid="loan-tool"
            >
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center mb-3">
                <Banknote className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <h4 className={`font-semibold text-sm sm:text-base mb-1 ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                Loan Calculator
              </h4>
              <p className={`text-xs ${isDark ? 'text-white/50' : 'text-ux-charcoal/50'}`}>
                Check eligibility
              </p>
            </Link>

            {/* SOP Generator */}
            <Link 
              to={user?.has_application ? "/uac-desk/sop" : "#"}
              onClick={(e) => !user?.has_application && e.preventDefault()}
              className={`group p-4 sm:p-5 rounded-xl sm:rounded-2xl transition-all ${
                user?.has_application 
                  ? 'hover:scale-[1.02]' 
                  : 'opacity-60 cursor-not-allowed'
              } ${isDark ? 'bg-white/5' : 'bg-white border border-gray-200'}`}
              data-testid="sop-tool"
            >
              <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center mb-3 ${
                user?.has_application 
                  ? 'bg-gradient-to-br from-rose-500 to-pink-600' 
                  : 'bg-gray-200'
              }`}>
                <FileText className={`w-5 h-5 sm:w-6 sm:h-6 ${user?.has_application ? 'text-white' : 'text-gray-400'}`} />
              </div>
              <h4 className={`font-semibold text-sm sm:text-base mb-1 flex items-center gap-2 ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                SOP Generator
                {!user?.has_application && <Lock className="w-3 h-3 text-gray-400" />}
              </h4>
              <p className={`text-xs ${isDark ? 'text-white/50' : 'text-ux-charcoal/50'}`}>
                {user?.has_application ? 'AI writing' : 'Apply first'}
              </p>
            </Link>

            {/* Saved Universities */}
            <Link 
              to="/uac-desk/saved"
              className={`group p-4 sm:p-5 rounded-xl sm:rounded-2xl transition-all hover:scale-[1.02] ${
                isDark ? 'bg-white/5 hover:bg-white/10' : 'bg-white hover:shadow-lg border border-gray-200'
              }`}
              data-testid="saved-tool"
            >
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center mb-3">
                <Bookmark className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <h4 className={`font-semibold text-sm sm:text-base mb-1 ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                Saved
              </h4>
              <p className={`text-xs ${isDark ? 'text-white/50' : 'text-ux-charcoal/50'}`}>
                {savedUniversities.length} bookmarked
              </p>
            </Link>

            {/* Resource Library */}
            <Link 
              to="/uac-desk/resources"
              className={`group p-4 sm:p-5 rounded-xl sm:rounded-2xl transition-all hover:scale-[1.02] ${
                isDark ? 'bg-white/5 hover:bg-white/10' : 'bg-white hover:shadow-lg border border-gray-200'
              }`}
              data-testid="resources-tool"
            >
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center mb-3">
                <BookOpen className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <h4 className={`font-semibold text-sm sm:text-base mb-1 ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                Resources
              </h4>
              <p className={`text-xs ${isDark ? 'text-white/50' : 'text-ux-charcoal/50'}`}>
                Guides & articles
              </p>
            </Link>

            {/* IELTS/PTE Practice */}
            <Link 
              to="/uac-desk/english-test"
              className={`group p-4 sm:p-5 rounded-xl sm:rounded-2xl transition-all hover:scale-[1.02] ${
                isDark ? 'bg-white/5 hover:bg-white/10' : 'bg-white hover:shadow-lg border border-gray-200'
              }`}
              data-testid="english-test-tool"
            >
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center mb-3">
                <Languages className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <h4 className={`font-semibold text-sm sm:text-base mb-1 ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                IELTS/PTE
              </h4>
              <p className={`text-xs ${isDark ? 'text-white/50' : 'text-ux-charcoal/50'}`}>
                Practice tests
              </p>
            </Link>
          </div>
        </section>

        {/* ========== RECENT ACTIVITY ========== */}
        <section className="mb-6 sm:mb-8">
          <RecentActivity userId={user?.user_id} limit={8} />
        </section>

        {/* ========== SAVED UNIVERSITIES ========== */}
        {savedUniversities.length > 0 && (
          <section className="mb-6 sm:mb-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className={`text-sm font-medium uppercase tracking-wider flex items-center gap-2 ${isDark ? 'text-white/50' : 'text-ux-charcoal/50'}`}>
                <Bookmark className="w-4 h-4" />
                Bookmarked Universities
              </h3>
              <Link to="/destinations" className="text-xs text-ux-cyan hover:underline flex items-center gap-1">
                Browse more <ChevronRight className="w-3 h-3" />
              </Link>
            </div>
            
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              {savedUniversities.slice(0, 6).map((uni) => (
                <Card 
                  key={uni.slug}
                  className={`relative group overflow-hidden transition-all hover:shadow-lg ${
                    isDark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'
                  }`}
                >
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1 min-w-0 pr-2">
                        <h4 className={`font-semibold text-sm truncate ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                          {uni.university_name}
                        </h4>
                        <div className="flex items-center gap-1 text-xs mt-1">
                          <MapPin className={`w-3 h-3 ${isDark ? 'text-white/40' : 'text-ux-charcoal/40'}`} />
                          <span className={`truncate ${isDark ? 'text-white/60' : 'text-ux-charcoal/60'}`}>
                            {uni.city}, {uni.country}
                          </span>
                        </div>
                      </div>
                      {uni.world_ranking && (
                        <Badge className="bg-amber-100 text-amber-700 text-xs shrink-0">
                          #{uni.world_ranking}
                        </Badge>
                      )}
                    </div>
                    
                    <div className="flex items-center justify-between pt-2 border-t border-dashed border-gray-200 dark:border-white/10">
                      <Link 
                        to={`/universities/${uni.slug}`}
                        className="text-xs font-medium flex items-center gap-1 text-ux-cyan hover:underline"
                      >
                        View <ExternalLink className="w-3 h-3" />
                      </Link>
                      <button
                        onClick={() => handleRemoveSaved(uni.slug, uni.university_name)}
                        className={`p-1.5 rounded-full transition-colors ${
                          isDark 
                            ? 'hover:bg-red-500/20 text-white/40 hover:text-red-400' 
                            : 'hover:bg-red-50 text-gray-400 hover:text-red-500'
                        }`}
                        title="Remove"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>
        )}

        {/* ========== PROFILE QUICK ACCESS ========== */}
        <section>
          <Link 
            to="/uac-desk/fast-track"
            className={`block p-4 sm:p-5 rounded-xl sm:rounded-2xl transition-all hover:scale-[1.01] ${
              isDark ? 'bg-white/5 hover:bg-white/10' : 'bg-gray-50 hover:bg-gray-100'
            }`}
            data-testid="profile-link"
          >
            <div className="flex items-center gap-4">
              <Avatar className="w-12 h-12 ring-2 ring-ux-cyan/30">
                <AvatarImage src={user.picture} />
                <AvatarFallback className="bg-gradient-to-br from-ux-violet to-ux-rose text-white">
                  {user.name?.charAt(0) || 'U'}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className={`font-medium ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                  {user.name}
                </p>
                <p className={`text-sm ${isDark ? 'text-white/50' : 'text-ux-charcoal/50'}`}>
                  {user.has_profile ? 'View & update your profile' : 'Complete your profile to get started'}
                </p>
              </div>
              <ChevronRight className={`w-5 h-5 ${isDark ? 'text-white/40' : 'text-ux-charcoal/40'}`} />
            </div>
          </Link>
        </section>
      </main>
      
      {/* Search Limit Modal */}
      {showLimitModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={`rounded-2xl w-full max-w-md overflow-hidden ${isDark ? 'bg-[#233842]' : 'bg-white'}`}>
            <div className="p-4 border-b bg-gradient-to-r from-rose-500 to-red-500">
              <h3 className="font-semibold text-lg text-white flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                Search Limit Reached
              </h3>
            </div>
            
            <div className="p-6 text-center">
              <div className="w-16 h-16 rounded-full bg-rose-100 flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-8 h-8 text-rose-500" />
              </div>
              <h4 className={`font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                You've used all 5 AI searches
              </h4>
              <p className={`text-sm ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
                Chat with your counsellor for unlimited guidance, or check your saved searches.
              </p>
            </div>
            
            <div className={`p-4 border-t flex gap-2 ${isDark ? 'border-white/10 bg-white/5' : 'bg-gray-50'}`}>
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => setShowLimitModal(false)}
              >
                Close
              </Button>
              <Button 
                className="flex-1 bg-gradient-to-r from-ux-green to-emerald-500 text-white"
                onClick={() => {
                  setShowLimitModal(false);
                  // Trigger Zoho SalesIQ chat
                  const floatBtn = document.getElementById('zsiq_float');
                  if (floatBtn) {
                    floatBtn.click();
                  } else if (window.$zoho && window.$zoho.salesiq) {
                    window.$zoho.salesiq.floatwindow.visible('show');
                  }
                }}
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                Chat with Counsellor
              </Button>
            </div>
          </div>
        </div>
      )}
      </div>
      </PortalLeadGate>
    </>
  );
};

export default StudentDashboard;
